/**
 * Collection tile view (PP-0) — the builder for LEADING and TRAILING tiles: the
 * things a collection can offer that are not records. "Copied section", "Blank
 * section", "New project", "Upload" all render here, in the same track as the
 * records, so that anything a user can click to insert or create is one shape.
 *
 * Accessibility model, chosen over a focusable div: the tile CONTAINER is
 * non-interactive (no role, no tabindex). Inside it, a real primary <button>
 * wraps poster + text and IS the tile; an optional sibling dismiss <button>
 * sits in `.pb-coll-actions`. No nested interactives, native Enter/Space, and
 * the roving tabindex (grid-key-nav `focusTargetOf`) lands on the primary.
 *
 * DOM-first like the item view: every caller string lands via textContent.
 */

import { createButton } from './components.js';
import { iconEl } from './icons.js';
import { applyThumbnail } from '../shared/thumbnail-url.js';

export const TILE_KINDS = Object.freeze(['create', 'clipboard', 'custom']);

/**
 * Validate a tiles option. Throws on the shapes that would otherwise fail
 * silently at click time — the same posture as the engine's own
 * `requires load()` checks.
 *
 * @param {{leading?: object[], trailing?: object[]}|null|undefined} tiles
 * @returns {{leading: object[], trailing: object[]}}
 */
export function normalizeTiles(tiles) {
  const out = { leading: [], trailing: [] };
  if (tiles == null) return out;
  if (typeof tiles !== 'object') throw new Error('tiles must be an object');
  const seen = new Set();
  for (const side of ['leading', 'trailing']) {
    const list = tiles[side];
    if (list == null) continue;
    if (!Array.isArray(list)) throw new Error(`tiles.${side} must be an array`);
    for (const spec of list) {
      if (!spec || typeof spec !== 'object')
        throw new Error(`tiles.${side}: tile is not an object`);
      const id = spec.id;
      if (typeof id !== 'string' || !id.trim())
        throw new Error('tile.id must be a non-empty string');
      if (seen.has(id)) throw new Error(`duplicate tile id "${id}"`);
      seen.add(id);
      if (!TILE_KINDS.includes(spec.kind)) {
        throw new Error(`tile "${id}": kind must be one of ${TILE_KINDS.join(', ')}`);
      }
      if (typeof spec.onActivate !== 'function') {
        throw new Error(`tile "${id}": onActivate must be a function`);
      }
      if (spec.onDismiss != null && typeof spec.onDismiss !== 'function') {
        throw new Error(`tile "${id}": onDismiss must be a function`);
      }
      if (spec.matchesQuery != null && typeof spec.matchesQuery !== 'function') {
        throw new Error(`tile "${id}": matchesQuery must be a function`);
      }
      out[side].push(spec);
    }
  }
  return out;
}

function buildGlyph(kind) {
  const glyph = document.createElement('div');
  glyph.className = 'pb-coll-tile-glyph';
  glyph.setAttribute('aria-hidden', 'true');
  // `plus` and `duplicate` both exist in the registry; iconEl returns null in
  // a DOM-less context, and a text fallback keeps the box from being empty.
  const svg = iconEl(kind === 'create' ? 'plus' : 'duplicate');
  if (svg) glyph.appendChild(svg);
  else glyph.textContent = kind === 'create' ? '+' : '⧉';
  return glyph;
}

/**
 * @param {object} spec — a validated TileSpec
 * @param {{layout?: 'grid'|'rows', itemTag?: string, presentation?: boolean}} [opts]
 * @returns {{el: HTMLElement, primaryBtn: HTMLButtonElement, dismissBtn: HTMLButtonElement|null, posterEl: HTMLElement}}
 */
export function renderCollectionTile(spec, opts = {}) {
  const { layout = 'grid', itemTag = 'li', presentation = false } = opts;
  const isRow = layout === 'rows';

  const el = document.createElement(itemTag);
  el.className = `pb-coll-item ${isRow ? 'pb-coll-row' : 'pb-coll-card'} pb-coll-tile pb-coll-tile-${spec.kind}`;
  el.dataset.cbTile = spec.id;
  if (spec.testid) el.dataset.testid = spec.testid;
  // Under a listbox the container must not read as an option; under grid
  // semantics it needs nothing at all.
  if (presentation) el.setAttribute('role', 'presentation');

  const poster = document.createElement('div');
  poster.className = 'pb-coll-poster';
  let img = null;
  if (spec.thumbnail) {
    img = document.createElement('img');
    img.alt = '';
    applyThumbnail(img, spec.thumbnail, {
      onExhausted: () => {
        img.remove();
        poster.appendChild(buildGlyph(spec.kind));
      }
    });
    poster.appendChild(img);
  } else {
    poster.appendChild(buildGlyph(spec.kind));
  }
  if (spec.kind === 'clipboard') {
    const tag = document.createElement('span');
    tag.className = 'pb-coll-tile-tag';
    tag.textContent = 'Copied';
    poster.appendChild(tag);
  }

  const text = document.createElement('div');
  text.className = 'pb-coll-text';
  const title = document.createElement('div');
  title.className = 'pb-coll-title';
  title.textContent = String(spec.label ?? '');
  text.appendChild(title);
  if (spec.description) {
    const meta = document.createElement('div');
    meta.className = 'pb-coll-meta-line';
    meta.textContent = String(spec.description);
    text.appendChild(meta);
  }

  const primaryBtn = createButton({
    variant: 'bare',
    className: 'pb-coll-tile-primary',
    content: [poster, text],
    ariaLabel: spec.description ? `${spec.label}, ${spec.description}` : String(spec.label ?? ''),
    dataset: { cbTileActivate: spec.id }
  });
  primaryBtn.tabIndex = -1;
  el.appendChild(primaryBtn);

  let dismissBtn = null;
  if (typeof spec.onDismiss === 'function') {
    const actions = document.createElement('div');
    actions.className = 'pb-coll-actions';
    dismissBtn = createButton({
      variant: 'ghost',
      size: 'sm',
      icon: iconEl('close')?.outerHTML || '×',
      ariaLabel: 'Dismiss',
      tooltip: 'Dismiss',
      className: 'pb-coll-tile-dismiss',
      dataset: { cbTileDismiss: spec.id }
    });
    actions.appendChild(dismissBtn);
    el.appendChild(actions);
  }

  return { el, primaryBtn, dismissBtn, posterEl: poster };
}
